import { useId, useState } from "react";
import { Link } from "react-router";

import { ApiError, redeemShareCode } from "../lib/api.ts";
import { REDEEM_COPY } from "./shareCodes.constants.ts";
import "./shareCodes.css";

/**
 * The door in, from the reader's side: a box to type a code into.
 *
 * Self-contained rather than a controller of its own, because it holds three
 * things — a string, a busy flag and an outcome — and a screen mounting it
 * needs nothing back but where the work is.
 *
 * A LINK IN THE DONE STATE, not a navigation. The register wizard hands off to
 * the record the same way: the reader is told what happened and offered the
 * way there, rather than being moved before they have read the sentence.
 *
 * TWO REFUSALS, KEPT APART. 403 is "that code cannot be redeemed" and covers
 * every reason at once, because the door does not say which — see decision 5
 * of the migration. 429 is a spent guessing budget, and it is said in
 * different words because a throttle rendered as "wrong code" would tell the
 * reader the opposite of the truth about the guess they just made.
 */
export function RedeemShareCode({ workBase }: { workBase: string }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<
    { kind: "opened"; href: string } | { kind: "refused"; message: string } | null
  >(null);
  const id = useId();

  return (
    <section className="redeem">
      <h2 className="redeem__title">{REDEEM_COPY.title}</h2>
      <p className="redeem__lead">{REDEEM_COPY.lead}</p>

      <form
        className="redeem__form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!code.trim()) return;
          setBusy(true);
          setOutcome(null);

          redeemShareCode({ code })
            .then(({ workId }) => {
              setOutcome({ kind: "opened", href: `${workBase}/${workId}` });
              setCode("");
            })
            .catch((cause: unknown) => {
              const message =
                cause instanceof ApiError && cause.status === 429
                  ? REDEEM_COPY.throttled
                  : cause instanceof ApiError && cause.isUnreachable
                    ? cause.message
                    : REDEEM_COPY.refused;
              setOutcome({ kind: "refused", message });
            })
            .finally(() => setBusy(false));
        }}
      >
        <label className="redeem__label" htmlFor={id}>
          {REDEEM_COPY.label}
        </label>
        <div className="redeem__row">
          <input
            id={id}
            className="redeem__input"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={REDEEM_COPY.placeholder}
            autoComplete="off"
            spellCheck={false}
            maxLength={40}
            disabled={busy}
          />
          <button type="submit" className="redeem__submit" disabled={busy || !code.trim()}>
            {busy ? REDEEM_COPY.opening : REDEEM_COPY.submit}
          </button>
        </div>
      </form>

      {outcome?.kind === "opened" && (
        <p className="redeem__outcome redeem__outcome--opened" role="status">
          {REDEEM_COPY.opened}{" "}
          <Link className="redeem__view" to={outcome.href}>
            {REDEEM_COPY.view}
          </Link>
        </p>
      )}
      {outcome?.kind === "refused" && (
        <p className="redeem__outcome redeem__outcome--refused" role="alert">
          {outcome.message}
        </p>
      )}
    </section>
  );
}
